import { createSlice } from '@reduxjs/toolkit';
import {
    createPublishAsCompositionThunk,
    createUnpublishAsCompositionThunk,
    createLoadElearningItemsThunk,
    createLoadElearningItemThunk,
    createNewElearningItemThunk,
    deleteElearningItemThunk,
    saveElearningItemThunk,
} from '../elearning/elearning.js';
import { assignAndIsDirty } from '../../helpers/extend.js';

export const loadYoutubeItemsIfNeeded = createLoadElearningItemsThunk('youtube', 'youtube');

export const loadYoutubeItem = createLoadElearningItemThunk('youtube');

export const publishYoutubeItemAsComposition = createPublishAsCompositionThunk('youtube');

export const unpublishYoutubeItemAsComposition = createUnpublishAsCompositionThunk('youtube');

export const createYoutubeItem = createNewElearningItemThunk('youtube', 'youtube');

export const deleteYoutubeItem = deleteElearningItemThunk('youtube');

export const saveYoutubeItem = saveElearningItemThunk('youtube', 'youtube');

const youtubeSlice = createSlice({
    name: 'youtube',
    initialState: {
        status: 'idle',
        list: [],
        page: -1,
        error: null
    },
    reducers: {
        updateYoutubeItem: (state, action) => {
            const { payload } = action;

            if (assignAndIsDirty(state.current, payload.changes)) {
                state.dirty = true;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadYoutubeItemsIfNeeded.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadYoutubeItemsIfNeeded.fulfilled, (state, action) => {
                const { payload } = action;

                state.status = 'success';
                state.list = payload.reload ? [...payload.list] : [...state.list, ...payload.list];
                state.page = payload.page;
                state.total = payload.total;
                state.search = payload.search;
            })
            .addCase(loadYoutubeItemsIfNeeded.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(loadYoutubeItem.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadYoutubeItem.fulfilled, (state, action) => {
                const { payload } = action;
                const numVersions = payload.versions.length;

                state.status = 'success';
                state.current = {
                    ...payload,
                    lastVersion: numVersions > 0 ? payload.versions[numVersions - 1] : {}
                };
                state.dirty = false;
            })
            .addCase(loadYoutubeItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(publishYoutubeItemAsComposition.pending, (state) => {
                state.status = 'publishing';
            })
            .addCase(publishYoutubeItemAsComposition.fulfilled, (state, action) => {
                const { payload } = action;
                let youtubeItem = state.list.find((i) => i.targetId === payload.targetId);

                state.status = 'success';
                youtubeItem = Object.assign(youtubeItem, payload);
            })
            .addCase(publishYoutubeItemAsComposition.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(unpublishYoutubeItemAsComposition.pending, (state) => {
                state.status = 'unpublishing';
            })
            .addCase(unpublishYoutubeItemAsComposition.fulfilled, (state, action) => {
                const { payload } = action;
                let ltiItem = state.list.find((i) => i.targetId === payload.targetId);

                state.status = 'success';
                ltiItem = Object.assign(ltiItem, payload);
            })
            .addCase(unpublishYoutubeItemAsComposition.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(createYoutubeItem.pending, (state) => {
                state.status = 'creating';
            })
            .addCase(createYoutubeItem.fulfilled, (state, action) => {
                const { payload } = action;

                state.status = 'success';
                state.list.unshift(payload);
                state.total++;
            })
            .addCase(createYoutubeItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(deleteYoutubeItem.pending, (state) => {
                state.status = 'deleting';
            })
            .addCase(deleteYoutubeItem.fulfilled, (state, action) => {
                const { payload } = action;

                state.status = 'success';
                state.list = state.list.filter((i) => i.targetId !== payload.targetId);
                state.total--;
            })
            .addCase(deleteYoutubeItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(saveYoutubeItem.pending, (state) => {
                state.status = 'saving';
            })
            .addCase(saveYoutubeItem.fulfilled, (state, action) => {
                const { payload } = action;
                const numVersions = payload.versions.length;

                state.status = 'success';
                state.current = {
                    ...payload,
                    lastVersion: numVersions > 0 ? payload.versions[numVersions - 1] : {}
                };
                state.dirty = false;
            })
            .addCase(saveYoutubeItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            });
    },
});

export const { updateYoutubeItem } = youtubeSlice.actions;

export default youtubeSlice.reducer;
